import * as types from "../../mutation-types";

function taskValue(task) {
	const v = Number(task?.value);
	return Number.isNaN(v) ? 0 : v;
}

// Adjust column totals by delta against the API-backed aggregates. Do not
// recompute from the loaded (paginated) page — that under-reports large steps.
function adjustStepValue(state, stepId, delta) {
	if (!delta) return;
	const step = state.steps.find((s) => String(s.id) === String(stepId));
	if (!step) return;

	const current = Number(step.value_sum) || 0;
	const next = current + delta;
	step.value_sum = next > 0 ? next : undefined;
	step.weighted_value_sum =
		next > 0 ? (next * (step.probability ?? 100)) / 100 : undefined;
}

export default {
	[types.SET_BOARDS](state, boards) {
		state.boards = boards;
	},
	[types.SET_SELECTED_BOARD_ID](state, id) {
		state.selectedBoardId = id;
	},
	[types.SET_STEPS](state, steps) {
		state.steps = steps;
	},
	[types.SET_KANBAN_LOADING](state, isLoading) {
		state.isLoading = isLoading;
	},
	[types.SET_BOARDS_LOADED](state, boardsLoaded) {
		state.boardsLoaded = boardsLoaded;
	},
	[types.SET_BOARDS_FETCH_FAILED](state, boardsFetchFailed) {
		state.boardsFetchFailed = boardsFetchFailed;
	},
	[types.INCREMENT_BOARDS_REQUEST_VERSION](state) {
		state.boardsRequestVersion += 1;
	},
	[types.RESET_KANBAN_ACCOUNT_STATE](state, accountId) {
		state.boardsAccountId = accountId;
		state.boards = [];
		state.selectedBoardId = null;
		state.steps = [];
		state.preferences = {};
		state.productsByBoardId = {};
		state.stepTasks = {};
		state.stepMeta = {};
		state.stepLoading = {};
		state.stepFetched = {};
		Object.keys(state.stepRequestVersion).forEach((stepId) => {
			state.stepRequestVersion[stepId] += 1;
		});
		state.stepsRequestVersion += 1;
	},
	[types.ADD_TASK](state, task) {
		const stepId = task.board_step_id;
		if (!state.stepTasks[stepId]) {
			state.stepTasks[stepId] = [];
		}
		if (!state.stepTasks[stepId].some((t) => t.id === task.id)) {
			state.stepTasks[stepId] = [task, ...state.stepTasks[stepId]];

			const step = state.steps.find((s) => String(s.id) === String(stepId));
			if (step) {
				step.tasks_count += 1;
			}
			adjustStepValue(state, stepId, taskValue(task));
		}
	},
	[types.UPDATE_TASK](state, updatedTask) {
		// Find old task in stepTasks
		let oldTask = null;
		let oldStepId = null;
		const stepIds = Object.keys(state.stepTasks);
		stepIds.some((stepId) => {
			const found = state.stepTasks[stepId]?.find(
				(t) => t.id === updatedTask.id,
			);
			if (found) {
				oldTask = found;
				oldStepId = stepId;
				return true;
			}
			return false;
		});

		if (!oldTask) return;

		// Merge incoming data with existing task to preserve fields not in the payload
		const mergedTask = { ...oldTask, ...updatedTask };
		const newStepId = mergedTask.board_step_id;
		const valueDelta = taskValue(mergedTask) - taskValue(oldTask);

		if (oldStepId !== String(newStepId) && oldStepId !== newStepId) {
			// Moving to different step
			state.stepTasks[oldStepId] = state.stepTasks[oldStepId].filter(
				(t) => t.id !== mergedTask.id,
			);
			if (!state.stepTasks[newStepId]) {
				state.stepTasks[newStepId] = [];
			}
			state.stepTasks[newStepId] = [
				...state.stepTasks[newStepId].filter((t) => t.id !== mergedTask.id),
				mergedTask,
			];

			const oldStep = state.steps.find(
				(s) => String(s.id) === String(oldStepId),
			);
			if (oldStep && oldStep.tasks_count > 0) {
				oldStep.tasks_count -= 1;
			}
			const newStep = state.steps.find(
				(s) => String(s.id) === String(newStepId),
			);
			if (newStep) {
				newStep.tasks_count += 1;
			}

			adjustStepValue(state, oldStepId, -taskValue(oldTask));
			adjustStepValue(state, newStepId, taskValue(mergedTask));
		} else {
			// Replace the array so column watchers (shallow) pick up field changes
			// like value / has_products without requiring a page refresh.
			state.stepTasks[oldStepId] = state.stepTasks[oldStepId].map((t) =>
				t.id === mergedTask.id ? mergedTask : t,
			);

			if (valueDelta !== 0) {
				adjustStepValue(state, oldStepId, valueDelta);
			}
		}
	},
	[types.MOVE_TASK](
		state,
		{ task, sourceStepId, destinationStepId, insertBeforeTaskId },
	) {
		const existing =
			state.stepTasks[sourceStepId]?.find((t) => t.id === task.id) || task;

		// Remove from source step
		if (state.stepTasks[sourceStepId]) {
			state.stepTasks[sourceStepId] = state.stepTasks[sourceStepId].filter(
				(t) => t.id !== task.id,
			);
		}

		// Initialize destination step if needed
		if (!state.stepTasks[destinationStepId]) {
			state.stepTasks[destinationStepId] = [];
		}

		// Insert into destination step
		if (insertBeforeTaskId) {
			const index = state.stepTasks[destinationStepId].findIndex(
				(t) => t.id === insertBeforeTaskId,
			);
			if (index !== -1) {
				state.stepTasks[destinationStepId].splice(index, 0, task);
			} else {
				state.stepTasks[destinationStepId].push(task);
			}
		} else {
			state.stepTasks[destinationStepId].push(task);
		}

		// Update counts and value sums if moving between steps
		if (sourceStepId !== destinationStepId) {
			const oldStep = state.steps.find(
				(s) => String(s.id) === String(sourceStepId),
			);
			if (oldStep && oldStep.tasks_count > 0) {
				oldStep.tasks_count -= 1;
			}
			const newStep = state.steps.find(
				(s) => String(s.id) === String(destinationStepId),
			);
			if (newStep) {
				newStep.tasks_count += 1;
			}
			const movedValue = taskValue(existing);
			adjustStepValue(state, sourceStepId, -movedValue);
			adjustStepValue(state, destinationStepId, taskValue(task) || movedValue);
		}
	},
	[types.DELETE_TASK](state, taskId) {
		const stepIds = Object.keys(state.stepTasks);
		stepIds.some((stepId) => {
			const task = state.stepTasks[stepId]?.find((t) => t.id === taskId);
			if (task) {
				state.stepTasks[stepId] = state.stepTasks[stepId].filter(
					(t) => t.id !== taskId,
				);

				const step = state.steps.find((s) => String(s.id) === String(stepId));
				if (step && step.tasks_count > 0) {
					step.tasks_count -= 1;
				}
				adjustStepValue(state, stepId, -taskValue(task));
				return true;
			}
			return false;
		});
	},
	[types.UPDATE_STEP](state, updatedStep) {
		const index = state.steps.findIndex((s) => s.id === updatedStep.id);
		if (index !== -1) {
			const existing = state.steps[index];
			// Preserve value_sum/weighted_value_sum from index endpoint when not in update payload
			const merged = { ...existing, ...updatedStep };
			if (!("value_sum" in updatedStep) && existing.value_sum != null) {
				// Recalculate weighted if probability changed
				merged.weighted_value_sum =
					(existing.value_sum * (merged.probability ?? 100)) / 100;
			}
			state.steps.splice(index, 1, merged);
		}
	},
	[types.ADD_STEP](state, step) {
		if (!state.steps.some((s) => s.id === step.id)) {
			state.steps.push(step);
		}
	},
	[types.ADD_BOARD](state, board) {
		state.boards.push(board);
	},
	[types.UPDATE_BOARD](state, updatedBoard) {
		const index = state.boards.findIndex((f) => f.id === updatedBoard.id);
		if (index !== -1) {
			state.boards.splice(index, 1, updatedBoard);
		}
	},
	[types.DELETE_BOARD](state, boardId) {
		const index = state.boards.findIndex((f) => f.id === boardId);
		if (index !== -1) {
			state.boards.splice(index, 1);
		}
	},
	[types.SET_KANBAN_PREFERENCES](state, preferences) {
		state.preferences = { ...state.preferences, ...preferences };
	},

	// Step-based task loading mutations
	[types.SET_STEP_TASKS](state, { stepId, tasks }) {
		state.stepTasks = { ...state.stepTasks, [stepId]: tasks };
		state.stepFetched = { ...state.stepFetched, [stepId]: true };
	},
	[types.APPEND_STEP_TASKS](state, { stepId, tasks }) {
		const existing = state.stepTasks[stepId] || [];
		const newTaskIds = new Set(tasks.map((t) => t.id));
		const filtered = existing.filter((t) => !newTaskIds.has(t.id));
		state.stepTasks = { ...state.stepTasks, [stepId]: [...filtered, ...tasks] };
	},
	[types.SET_STEP_LOADING](state, { stepId, isLoading }) {
		state.stepLoading = { ...state.stepLoading, [stepId]: isLoading };
	},
	[types.SET_STEP_META](state, { stepId, meta }) {
		state.stepMeta = { ...state.stepMeta, [stepId]: meta };
	},
	[types.RESET_STEP_TASKS](state) {
		state.stepTasks = {};
		state.stepMeta = {};
		state.stepLoading = {};
		state.stepFetched = {};
		state.stepRequestVersion = {};
	},
	[types.INCREMENT_STEPS_REQUEST_VERSION](state) {
		state.stepsRequestVersion += 1;
	},
	[types.INCREMENT_STEP_REQUEST_VERSION](state, stepId) {
		const current = state.stepRequestVersion[stepId] || 0;
		state.stepRequestVersion = {
			...state.stepRequestVersion,
			[stepId]: current + 1,
		};
	},

	// Products (scoped by board so switching funnels does not clobber catalogs)
	[types.SET_PRODUCTS](state, { boardId, products }) {
		state.productsByBoardId = {
			...state.productsByBoardId,
			[boardId]: products,
		};
	},
	[types.ADD_PRODUCT](state, { boardId, product }) {
		const key = boardId ?? product.board_id;
		if (key == null) return;
		const existing = state.productsByBoardId[key] || [];
		state.productsByBoardId = {
			...state.productsByBoardId,
			[key]: [product, ...existing],
		};
	},
	[types.UPDATE_PRODUCT](state, { boardId, product: updatedProduct }) {
		const key = boardId ?? updatedProduct.board_id;
		if (key == null) return;
		const list = state.productsByBoardId[key] || [];
		const index = list.findIndex((p) => p.id === updatedProduct.id);
		if (index === -1) return;
		const next = [...list];
		next.splice(index, 1, updatedProduct);
		state.productsByBoardId = { ...state.productsByBoardId, [key]: next };
	},
	[types.DELETE_PRODUCT](state, { boardId, productId }) {
		if (boardId == null) return;
		const list = state.productsByBoardId[boardId] || [];
		state.productsByBoardId = {
			...state.productsByBoardId,
			[boardId]: list.filter((p) => p.id !== productId),
		};
	},
};
